/**
 * R3F building layer: models by type and level (sites rising as they're
 * built), grown footprint tiles and fences, all through the shared
 * `ModelBatcher`; selection rings in one instanced mesh; own buildings picked
 * by the tile under the cursor. A rival's building and footprint tiles draw
 * only while their tile is visible (a tile out of sight is an instance
 * removed).
 */
import type { ThreeEvent } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialNeighbors, axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { Building, Cell } from '@/game/sim';
import { buildingLevel, isCity } from '@/game/sim';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';
import { cursorTileKey, ownClickTiles, unitTakesClick } from './clickPick';
import { flatRing, InstancedMarkers, type MarkerPoint } from './InstancedMarkers';
import {
  buildingLook,
  fenceLook,
  footprintLook,
  placeMatrix,
  useModelBatcher,
} from './ModelBatcher';
import { createBuildingPicker, unitHitOf } from './picking';

const THICKNESS = config.hexMap.tile.thickness;
const RADIUS = config.hexMap.tile.radius;
/** Height an unfinished building starts at, as a fraction of full height. */
const SITE_HEIGHT = 0.15;

const selectionRingGeometry = flatRing(0.85, 0.06, 36);
const selectionRingMaterial = new THREE.MeshStandardMaterial({
  color: '#fde047',
  emissive: '#fde047',
  emissiveIntensity: 1.4,
});

/** Fence sections sit this far out towards each edge, and span this much of it. */
const FENCE_INSET = 0.94;
/** The one fence section look every edge uses. */
export const FENCE_LOOK = fenceLook(RADIUS * FENCE_INSET);

/**
 * A grown building's extra tiles and a fence along every outer edge of the
 * whole footprint (farms and barracks), in world space. Only the tiles
 * `shown` (for a rival: in sight) get their parts.
 */
export function footprintParts(building: Building, shown: (cell: Cell) => boolean) {
  const cells = [building.pos, ...(building.footprint ?? [])];
  const inside = new Set(cells.map(getHexTileMapKey));
  const tiles = (building.footprint ?? []).filter(shown).map((c) => {
    const w = axialToWorld(c, RADIUS);
    return { key: getHexTileMapKey(c), x: w.x, z: w.z };
  });
  const fence: Array<{ key: string; x: number; z: number; yaw: number }> = [];
  if (building.type === 'farm' || building.type === 'barracks') {
    for (const c of cells.filter(shown)) {
      const w = axialToWorld(c, RADIUS);
      for (const n of axialNeighbors(c)) {
        if (inside.has(getHexTileMapKey(n))) continue;
        const nw = axialToWorld(n, RADIUS);
        const dx = (nw.x - w.x) / 2;
        const dz = (nw.z - w.z) / 2;
        fence.push({
          key: `${getHexTileMapKey(c)}>${getHexTileMapKey(n)}`,
          x: w.x + dx * FENCE_INSET,
          z: w.z + dz * FENCE_INSET,
          // The rail runs along local x: square it to the centre-to-edge direction.
          yaw: Math.atan2(dx, dz),
        });
      }
    }
  }
  return { tiles, fence };
}

interface BuildingsLayerProps {
  selectedBuildingId: string | null;
  /** Left-click on an own completed building. Omitted while placing, so clicks reach the tiles. */
  onSelectBuilding?: (id: string) => void;
}

const scratchMatrix = new THREE.Matrix4();

export function BuildingsLayer({ selectedBuildingId, onSelectBuilding }: BuildingsLayerProps) {
  const { view: state, playerId, playerOrder, rules, fog } = useSimFrame();
  const getTileVisibility = fog.visibility;
  const batcher = useModelBatcher();
  /** Entity ids this layer has in the batcher. */
  const placed = useRef(new Set<string>());
  /** Selectable tile → building id. */
  const selectTiles = useRef(new Map<string, string>());
  const picker = useMemo(() => createBuildingPicker(selectTiles), []);

  const buildings = Object.values(state.buildings);
  const clickTiles = onSelectBuilding ? ownClickTiles(state, rules, playerId) : null;

  // What to draw this tick: [entity id, look, x, z, yaw, scaleY, colour].
  const entities: Array<[string, string, number, number, number, number, string]> = [];
  const tiles = new Map<string, string>();
  const rings: MarkerPoint[] = [];
  for (const building of buildings) {
    // Own buildings always render. Enemy buildings render only on tiles
    // currently visible — no last-seen ghosts, and a grown farm's tiles
    // each show only while that tile is in sight.
    const own = building.owner === playerId;
    const shown = (cell: Cell) => own || getTileVisibility(getHexTileMapKey(cell)) === 'visible';
    const showMain = shown(building.pos);
    const color = PLAYER_COLORS[colorIndexFor(building.owner, playerOrder)] ?? PLAYER_COLORS[0];
    const level = buildingLevel(building);
    // Construction sites rise out of the ground as they're built.
    const height = SITE_HEIGHT + (1 - SITE_HEIGHT) * Math.min(1, building.buildProgress);
    const world = axialToWorld(building.pos, RADIUS);
    if (showMain) {
      entities.push([
        `b:${building.id}`,
        buildingLook(building.type, level),
        world.x,
        world.z,
        0,
        height,
        color,
      ]);
    }
    if (building.footprint) {
      const parts = footprintParts(building, shown);
      for (const t of parts.tiles) {
        entities.push([
          `bt:${building.id}:${t.key}`,
          footprintLook(building.type, level),
          t.x,
          t.z,
          0,
          height,
          color,
        ]);
      }
      for (const f of parts.fence) {
        entities.push([`bf:${building.id}:${f.key}`, FENCE_LOOK, f.x, f.z, f.yaw, height, color]);
      }
    }

    // Own completed buildings, and own sites other than cities (to cancel
    // them): every tile of a grown building takes the click and shows the
    // selection ring.
    const selectable = own && (building.buildProgress >= 1 || !isCity(building, rules));
    const isSelected = building.id === selectedBuildingId;
    if (!selectable && !isSelected) continue;
    for (const c of [building.pos, ...(building.footprint ?? [])]) {
      if (selectable && onSelectBuilding) tiles.set(getHexTileMapKey(c), building.id);
      if (isSelected) {
        const w = axialToWorld(c, RADIUS);
        rings.push({ x: w.x, y: THICKNESS + 0.03, z: w.z });
      }
    }
  }

  useLayoutEffect(() => {
    selectTiles.current = tiles;
    const keep = new Set<string>();
    for (const [id, look, x, z, yaw, scaleY, color] of entities) {
      keep.add(id);
      batcher.set(id, look, placeMatrix(scratchMatrix, x, THICKNESS, z, yaw, scaleY), color);
      placed.current.add(id);
    }
    for (const id of placed.current) {
      if (keep.has(id)) continue;
      batcher.remove(id);
      placed.current.delete(id);
    }
  });
  useEffect(
    () => () => {
      for (const id of placed.current) batcher.remove(id);
      placed.current.clear();
    },
    [batcher]
  );

  const onPointerDown = (e: ThreeEvent<PointerEvent>) => {
    // Right-click passes through to the tile (move orders onto the building).
    if (e.button !== 0 || !onSelectBuilding) return;
    const cursor = cursorTileKey(e.ray);
    const id = cursor === null ? undefined : selectTiles.current.get(cursor);
    if (cursor === null || id === undefined) return;
    // A unit under the cursor standing still on the tile the cursor is over
    // takes the click instead (clickPick.ts); one merely drawn in front, from
    // a neighbouring tile, doesn't. Its handler runs first and stops the
    // event when it takes it; this covers a hit sorted after ours.
    if (
      clickTiles &&
      e.intersections.some((i) => {
        const hit = unitHitOf(i);
        return hit !== null && unitTakesClick(hit.unitTile, cursor, clickTiles, hit.unitMoving);
      })
    )
      return;
    e.stopPropagation();
    onSelectBuilding(id);
  };

  return (
    <>
      {onSelectBuilding ? <primitive object={picker} onPointerDown={onPointerDown} /> : null}
      <InstancedMarkers
        geometry={selectionRingGeometry}
        material={selectionRingMaterial}
        points={rings}
      />
    </>
  );
}
